import { createContext, useContext, useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import { BarChart, HeatmapChart, LineChart, PieChart, ScatterChart, TreemapChart } from "echarts/charts";
import { GridComponent, LegendComponent, MarkAreaComponent, TooltipComponent, VisualMapContinuousComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { ResultEnvelope, Visualization } from "@cd/core";
import { buildChartOption, chartPick, type ChartClick, type Highlight } from "@cd/viz";
import { chartTheme, useResolvedTheme } from "../../lib/theme";
import type { OnPick } from "./onPick";

echarts.use([
  BarChart, HeatmapChart, LineChart, PieChart, ScatterChart, TreemapChart,
  GridComponent, LegendComponent, MarkAreaComponent, TooltipComponent, VisualMapContinuousComponent, CanvasRenderer,
]);

/**
 * How charts draw. The screen uses canvas with animation; a page that's printed sets SVG (sharp at
 * any size) and no animation, so a chart is finished as soon as it's drawn. SVG needs its renderer
 * registered (`echarts.use(SVGRenderer)`) by whoever asks for it.
 */
export const ChartRenderContext = createContext<{ renderer: "canvas" | "svg"; animation: boolean }>({ renderer: "canvas", animation: true });

export function Chart({ viz, result, onPick, highlight }: { viz: Visualization; result: ResultEnvelope; onPick?: OnPick; highlight?: Highlight[] }) {
  const el = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | null>(null);
  // The click handler is bound once per chart instance, so it reads the latest props from here.
  const latest = useRef({ viz, result, onPick });
  latest.current = { viz, result, onPick };
  const mode = useResolvedTheme();
  const { renderer, animation } = useContext(ChartRenderContext);

  useEffect(() => {
    if (!el.current) return;
    const c = echarts.init(el.current, undefined, { renderer });
    chart.current = c;
    let picked = false;
    c.on("click", (p) => {
      const { viz, result, onPick } = latest.current;
      const e = (p as { event?: { event?: MouseEvent } }).event?.event;
      if (!onPick || !e) return;
      const picks = chartPick(viz, result, p as ChartClick);
      if (picks.length) onPick(picks, { x: e.clientX, y: e.clientY });
      picked = picks.length > 0;
    });
    // Lines draw no points, so a click on one seldom lands on a data item: a click anywhere over a
    // category picks it. ECharts handles its own (series) clicks first, so `picked` is already set.
    c.getZr().on("click", (z) => {
      const handled = picked;
      picked = false;
      const { viz, result, onPick } = latest.current;
      const e = z.event as MouseEvent | undefined;
      if (handled || !onPick || !e || !["line", "area", "bar", "combo"].includes(viz.type)) return;
      const at = [z.offsetX, z.offsetY];
      if (!c.containPixel("grid", at)) return;
      const [x, y] = c.convertFromPixel("grid", at) as number[];
      const index = Math.round((viz.type === "bar" && viz.config.horizontal === true ? y : x) ?? NaN);
      if (!Number.isFinite(index) || index < 0) return;
      const picks = chartPick(viz, result, { dataIndex: index });
      if (picks.length) onPick(picks, { x: e.clientX, y: e.clientY });
    });
    const ro = new ResizeObserver(() => c.resize());
    ro.observe(el.current);
    return () => {
      ro.disconnect();
      c.dispose();
      chart.current = null;
    };
  }, [renderer]);

  useEffect(() => {
    const option = buildChartOption(viz, result, chartTheme(mode), highlight) as echarts.EChartsCoreOption;
    chart.current?.setOption(animation ? option : { ...option, animation: false }, true);
  }, [viz, result, mode, animation, highlight]);

  return <div ref={el} className="size-full min-h-0" role="img" aria-label="Chart" />;
}
